// WebAuthn browser ceremonies for the SAA passkey flow (identity stage C). The server (go-webauthn) sends the
// options with base64url-encoded binary members; the browser API needs ArrayBuffers, and the response goes back as
// base64url JSON in the standard PublicKeyCredential JSON shape the server parses.

const b64urlToBuf = (s: string): ArrayBuffer => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
};

const bufToB64url = (b: ArrayBuffer): string => {
  const bytes = new Uint8Array(b);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const passkeysSupported = (): boolean =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;

// createPasskey runs navigator.credentials.create with the server's creation options and returns the JSON body.
export async function createPasskey(options: any): Promise<object> {
  const pk = options.publicKey;
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...pk,
    challenge: b64urlToBuf(pk.challenge),
    user: { ...pk.user, id: b64urlToBuf(pk.user.id) },
    excludeCredentials: (pk.excludeCredentials || []).map((c: any) => ({ ...c, id: b64urlToBuf(c.id) })),
  };
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No passkey was created.');
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(r.clientDataJSON),
      attestationObject: bufToB64url(r.attestationObject),
      transports: typeof r.getTransports === 'function' ? r.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
}

// getAssertion runs navigator.credentials.get with the server's request options and returns the JSON body.
export async function getAssertion(options: any): Promise<object> {
  const pk = options.publicKey;
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...pk,
    challenge: b64urlToBuf(pk.challenge),
    allowCredentials: (pk.allowCredentials || []).map((c: any) => ({ ...c, id: b64urlToBuf(c.id) })),
  };
  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No passkey was used.');
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64url(r.clientDataJSON),
      authenticatorData: bufToB64url(r.authenticatorData),
      signature: bufToB64url(r.signature),
      userHandle: r.userHandle ? bufToB64url(r.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
}

// A cancelled / timed-out browser prompt reads as a user action, not an error.
export const passkeyErrorMessage = (e: unknown): string => {
  const n = (e as { name?: string })?.name;
  if (n === 'NotAllowedError' || n === 'AbortError') return 'The passkey prompt was cancelled or timed out.';
  if (n === 'InvalidStateError') return 'This authenticator is already registered.';
  return (e as Error)?.message || 'The passkey step failed.';
};

// Step-up (owner 2026-09-29): raise the CURRENT session to MFA with a passkey assertion — used when an organisation
// resource answers 403 MFA_CHALLENGE_REQUIRED. Returns true when the server confirmed the session.
export async function stepUpSession(apiUrl: (p: string) => string): Promise<boolean> {
  const b = await fetch(apiUrl('/api/auth/mfa/stepup/begin'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
  const bd = await b.json().catch(() => ({}));
  if (!b.ok) throw new Error(bd.error || 'The passkey step could not start.');
  const assertion = await getAssertion(bd.options);
  const f = await fetch(apiUrl(`/api/auth/mfa/stepup/finish?ceremony=${encodeURIComponent(bd.ceremony_id)}`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(assertion) });
  const fd = await f.json().catch(() => ({}));
  if (!f.ok) throw new Error(fd.error || 'The passkey could not be verified.');
  return fd.mfa_assurance === 'SATISFIED';
}
